import { useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronLeftIcon, ChevronRightIcon, PackageIcon, SearchIcon, ShoppingBagIcon, SlidersHorizontalIcon } from 'lucide-react';
import { PageHeader } from '../ui/PageHeader';
import { EmptyState } from '../ui/EmptyState';
import { LoadFailed } from '../ui/LoadState';
import { Badge } from '../ui/Badge';
import { Skeleton } from '../../ui/Skeleton';
import { btnSecondary, fadeWhileLoading, inputCls, listBox, rowLink } from '../ui/styles';
import { received } from '../inbox/inboxRules';
import { channelLabel, orderBadge, orderNumber, statusFilters } from './orderLabels';
import { money } from '../../core/format/currency';
import { useOrders } from './useOrders';
import { plural } from '../../core/format/text';
import { API_BASE } from '../../core/api/http';

/** Alle bestellingen, ook de bezorgde en geannuleerde. Nieuwste bovenaan; klik voor de details. */
export function Orders() {
  const {
    query,
    page,
    status,
    reload,
    setFilter,
    clearFilters,
    hasFilters,
    search,
    setSearch
  } = useOrders();
  const {
    search: listQuery
  } = useLocation();
  // Volgende pagina: terug naar de bovenkant van de lijst, niet onderaan blijven hangen.
  const listRef = useRef<HTMLUListElement>(null);
  const extraCount = [query.channel, query.from, query.to].filter(Boolean).length;
  const [more, setMore] = useState(false);
  const moreOpen = more || extraCount > 0;
  const goToPage = (p: number) => {
    setFilter('pagina', String(p));
    listRef.current?.scrollIntoView({
      block: 'start'
    });
  };
  return <div data-id="element-3585" data-mp="xkll705blq65">
      <PageHeader title="Bestellingen" description="Alles uit de webshop, ook wat al bezorgd is. Inpakken en verzenden kan in de inbox of in de bestelling zelf." actions={<Link to="/be/inbox" className={btnSecondary} data-id="element-3587" data-mp="17ec13gndssa8">
            <PackageIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-3588" data-mp="9wc2i73elij8" />
            Inpakken in de inbox
          </Link>} data-id="element-3586" data-mp="xkd8b8976kuy" />

      <form role="search" onSubmit={e => e.preventDefault()} className="mb-5 space-y-3" data-id="element-3589" data-mp="6vil5tg1qc1b">
        <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,14rem)_auto]" data-id="element-3590" data-mp="1wfi2ejmmfvz">
        <label className="relative block" data-id="element-3591" data-mp="1ucqvr6qmgpo">
          <span className="sr-only" data-id="element-3592" data-mp="glle5tn63pzn">Zoek op naam, e-mail of bestelnummer</span>
          <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" strokeWidth={1.75} aria-hidden="true" data-id="element-3593" data-mp="bmikt6gjv87i" />
          <input type="search" value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Escape' && search && setSearch('')} placeholder="Naam, e-mail of bestelnummer" className={`${inputCls} pl-9`} data-id="element-3594" data-mp="3k2kbv6jj7cwl" />
        </label>
        <label className="block" data-id="element-3595" data-mp="3p3hufk8ge8ym">
          <span className="sr-only" data-id="element-3596" data-mp="1997um2hhup3x">Status</span>
          <select value={query.status ?? ''} onChange={e => setFilter('status', e.target.value || undefined)} className={inputCls} data-id="element-3597" data-mp="19c3s5ppfjbru">
            <option value="" data-id="element-3598" data-mp="1bup2qb3gtpc4">Alle statussen</option>
            {statusFilters.map(f => <option key={f.value} value={f.value} data-id="element-3599" data-mp="1gt8p2clz1350">
                {f.label}
              </option>)}
          </select>
        </label>
        <button type="button" aria-expanded={moreOpen} aria-controls="order-more-filters" onClick={() => setMore(v => !v)} className={btnSecondary} data-id="element-3600" data-mp="12vbq59k1okn6">
          <SlidersHorizontalIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-3601" data-mp="1ul6ry5p3a2sq" />
          Meer filters
          {extraCount > 0 && <span className="rounded-full bg-clay px-1.5 text-[11px] leading-5 tabular-nums text-shell" data-id="element-3602" data-mp="1zioqd25hczmh">{extraCount}</span>}
        </button>
        </div>
        {/* Soort en periode zelden nodig: achter één knop, open zodra er een staat ingesteld. */}
        {moreOpen && <div id="order-more-filters" className="grid gap-3 sm:grid-cols-3" data-id="element-3603" data-mp="2dj9ca76ozbk7">
        <label className="block" data-id="element-3604" data-mp="2iahjqxogomuq">
          <span className="sr-only" data-id="element-3605" data-mp="2ibj6hzeuaxsa">Soort</span>
          <select value={query.channel ?? ''} onChange={e => setFilter('kanaal', e.target.value || undefined)} className={inputCls} data-id="element-3606" data-mp="2icc52d3nagzc">
            <option value="" data-id="element-3607" data-mp="pmju6i7lh44x">Webshop en abonnement</option>
            <option value="webshop" data-id="element-3608" data-mp="pp62ik2b7no7">{channelLabel.webshop}</option>
            <option value="abonnement" data-id="element-3609" data-mp="kortayoz27ol">{channelLabel.abonnement}</option>
          </select>
        </label>
        <label className="relative block" data-id="element-3610" data-mp="gnzgjvgw5knk">
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[13px] text-muted" data-id="element-3611" data-mp="1dcnv2hteeps8">Van</span>
          <input type="date" value={query.from ?? ''} max={query.to} onChange={e => setFilter('van', e.target.value || undefined)} className={`${inputCls} pl-12`} data-id="element-3612" data-mp="18dazn8ogoae1" />
        </label>
        <label className="relative block" data-id="element-3613" data-mp="zlisb6uqhhdo">
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[13px] text-muted" data-id="element-3614" data-mp="zkheiumc2gs9">Tot</span>
          <input type="date" value={query.to ?? ''} min={query.from} onChange={e => setFilter('tot', e.target.value || undefined)} className={`${inputCls} pl-12`} data-id="element-3615" data-mp="1w59oywkqw6a6" />
        </label>
        </div>}
      </form>

      <p className="mb-3 flex min-h-[1.5rem] flex-wrap items-center justify-between gap-2 text-[13px] text-muted" role="status" aria-live="polite" data-id="element-3616" data-mp="3ey9u7k3jjwq6">
        <span data-id="element-3617" data-mp="3iz3i4dgzhbj2">{status === 'loading' ? 'Bestellingen laden…' : page ? plural(page.total, 'bestelling', 'bestellingen') : ''}</span>
        {hasFilters && <button type="button" onClick={clearFilters} className="rounded-md px-1 text-ink underline underline-offset-4 hover:text-clay" data-id="element-3618" data-mp="3syatww82ky42">
            Filters wissen
          </button>}
      </p>

      {status === 'error' ? <LoadFailed title="De bestellingen konden niet laden" onRetry={reload} data-id="element-3619" data-mp="6jj3taypkjnz" /> : status === 'loading' && !page ? <ul className={listBox} aria-hidden="true" data-id="element-3620" data-mp="aja0fvax1u0f">
          {Array.from({
        length: 6
      }, (_, i) => <li key={i} className="flex items-center gap-4 px-4 py-4 sm:px-5" data-id="element-3621" data-mp="fkzb39u885nz">
              <Skeleton className="h-5 w-20 rounded-full" data-id="element-3622" data-mp="kj0trtnqq2yx" />
              <span className="flex-1 space-y-1.5" data-id="element-3623" data-mp="2gkujdf0ytlkh">
                <Skeleton className="h-3.5 w-40 rounded" data-id="element-3624" data-mp="2glnfzn67muow" />
                <Skeleton className="h-3 w-56 rounded" data-id="element-3625" data-mp="2bn2hptaiql33" />
              </span>
              <Skeleton className="h-4 w-16 rounded" data-id="element-3626" data-mp="2qaxaf9oj0pg6" />
            </li>)}
        </ul> : page && page.items.length === 0 ? <EmptyState icon={ShoppingBagIcon} title={hasFilters ? 'Geen bestellingen met deze filters' : 'Nog geen bestellingen'} text={hasFilters ? 'Probeer een andere zoekterm of periode.' : 'Zodra iemand in de webshop bestelt, staat het hier.'} action={hasFilters ? <button type="button" onClick={clearFilters} className={btnSecondary} data-id="element-3628" data-mp="397ysnw73cm22">
                Filters wissen
              </button> : undefined} data-id="element-3627" data-mp="2ljp1eer3z63j" /> : page ? <>
          <ul ref={listRef} className={`${listBox} scroll-mt-24 ${fadeWhileLoading(status === 'loading')}`} data-id="element-3629" data-mp="1exv3758bhc8o">
            {page.items.map(o => {
          const badge = orderBadge(o);
          return <li key={o.id} data-id="element-3630" data-mp="3qj42v892ioz1">
                  <Link to={`/be/bestellingen/${o.id}`} state={{
              from: listQuery
            }} className={rowLink} data-id="element-3631" data-mp="3qic5brvwuh64">
                    <span className="hidden w-28 shrink-0 sm:block" data-id="element-3632" data-mp="3btpspxxo43il">
                      <Badge tone={badge.tone} data-id="element-3633" data-mp="3gt2aw5dlgmeu">{badge.label}</Badge>
                    </span>
                    <span className="min-w-0 flex-1" data-id="element-3634" data-mp="32t9q0qor10es">
                      <span className="block truncate text-[14px] text-ink" data-id="element-3635" data-mp="32shu1er8p0nz">{o.customerName || o.email}</span>
                      <span className="block truncate text-[12px] text-muted" data-id="element-3636" data-mp="37qt3hodmh95o">
                        {orderNumber(o)} · {received(o.placedAt)}
                        {o.channel === 'abonnement' && ' · abonnement'}
                      </span>
                    </span>
                    <span className="shrink-0 text-right" data-id="element-3637" data-mp="r4rxryh78r0c">
                      <span className="block text-[14px] tabular-nums text-ink" data-id="element-3638" data-mp="w3v3zm8x139w">{money(o.totalCents)}</span>
                      {o.refundedCents > 0 && <span className="block text-[12px] tabular-nums text-muted" data-id="element-3639" data-mp="3fy2nvn9fcx9o">−{money(o.refundedCents)} terug</span>}
                      {/* Op de telefoon dezelfde gekleurde status, onder het bedrag. */}
                      <span className="mt-1 block sm:hidden" data-id="element-3640" data-mp="367zwtslv5tdz">
                        <Badge tone={badge.tone} data-id="element-3641" data-mp="31b9h56dz0zt3">{badge.label}</Badge>
                      </span>
                    </span>
                    <ChevronRightIcon className="h-4 w-4 shrink-0 text-muted" strokeWidth={1.5} aria-hidden="true" data-id="element-3642" data-mp="2wc69de6lwfhf" />
                  </Link>
                </li>;
        })}
          </ul>

          {page.pageCount > 1 && <nav aria-label="Pagina's" className="mt-4 flex items-center justify-between gap-3" data-id="element-3643" data-mp="2ncji3l7lcq23">
              <button type="button" disabled={page.page <= 1} onClick={() => goToPage(page.page - 1)} className={`${btnSecondary} disabled:cursor-not-allowed disabled:opacity-40`} data-id="element-3644" data-mp="2idyjfq7zmefr">
                <ChevronLeftIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-3645" data-mp="o301ixbycacp" />
                Vorige
              </button>
              <span className="text-[13px] tabular-nums text-muted" data-id="element-3646" data-mp="t0i508779xdb">
                Pagina {page.page} van {page.pageCount}
              </span>
              <button type="button" disabled={page.page >= page.pageCount} onClick={() => goToPage(page.page + 1)} className={`${btnSecondary} disabled:cursor-not-allowed disabled:opacity-40`} data-id="element-3647" data-mp="12rmjz7wvli85">
                Volgende
                <ChevronRightIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-3648" data-mp="f21wqctovyt2" />
              </button>
            </nav>}
        </> : null}

      {API_BASE === null && <p className="mt-8 text-[12px] text-muted" data-id="element-3649" data-mp="1qj1o9687q9m1">Dit zijn voorbeeldbestellingen uit de demo.</p>}
    </div>;
}